'use client';

import { startTransition, useCallback, useEffect, useState } from 'react';

// Lead distance for below-the-fold modules whose placeholder height can differ
// from the loaded content: activation (fetch, render, possible collapse) must
// resolve while the module is still below the viewport, so nothing visible
// shifts. Roughly two mobile viewports absorbs fetch latency at scroll speed.
export const BELOW_FOLD_ACTIVATION_ROOT_MARGIN = '1600px 0px';

type ViewportActivationOptions = {
  rootMargin?: string;
  idleFallbackMs?: number;
  disabled?: boolean;
  /** Activate from the observer in a transition, so a scroll burst that activates many nodes cannot block a tap. */
  deferObserverActivation?: boolean;
};

type ViewportActivationReturn<T extends Element> = {
  ref: (node: T | null) => void;
  isActive: boolean;
  activate: () => void;
  activateDeferred: () => void;
};

export function useViewportActivation<T extends Element>({
  rootMargin = '600px 0px',
  idleFallbackMs = 1500,
  disabled = false,
  deferObserverActivation = false,
}: ViewportActivationOptions = {}): ViewportActivationReturn<T> {
  const [node, setNode] = useState<T | null>(null);
  const [isActive, setIsActive] = useState(false);

  const activate = useCallback(() => {
    setIsActive(true);
  }, []);

  const activateDeferred = useCallback(() => {
    startTransition(() => setIsActive(true));
  }, []);

  useEffect(() => {
    if (disabled || isActive) return;

    if (typeof window === 'undefined') return;

    if (!('IntersectionObserver' in window)) {
      const id = setTimeout(activate, idleFallbackMs);
      return () => clearTimeout(id);
    }

    if (!node) return;

    const activateFromObserver = deferObserverActivation ? activateDeferred : activate;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          activateFromObserver();
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [activate, activateDeferred, deferObserverActivation, disabled, idleFallbackMs, isActive, node, rootMargin]);

  return { ref: setNode, isActive, activate, activateDeferred };
}
